// src/pages/Webinars.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import styled from "styled-components";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import PromptRibbon from "../components/Ribbon";
import WebinarSignupForm from "../components/WebinarSignupForm";

/* ===== API base ===== */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:5000";

/* ===== Layout Shell ===== */
const PageShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  background: #fff;
`;
const Main = styled.main`
  flex: 1 0 auto;
  padding-top: clamp(60px, 7vw, 80px);
`;

/* ===== Hero / Header ===== */
const HeaderSection = styled.section`
  position: relative;
  overflow: clip;
  padding: clamp(2.25rem, 5vw, 4rem) 1rem clamp(2rem, 4vw, 3rem);
  background: radial-gradient(
    1000px 600px at 10% -10%,
    #4e89aa 0%,
    #2a5a7a 45%,
    #244d66 70%,
    #1e3e52 100%
  );
  color: #fff;
  isolation: isolate;

  &::before,
  &::after {
    content: "";
    position: absolute;
    border-radius: 50%;
    filter: blur(48px);
    opacity: 0.25;
    pointer-events: none;
    transform: translateZ(0);
  }
  &::before {
    width: 520px;
    height: 520px;
    left: -160px;
    top: -180px;
    background: radial-gradient(
      circle at 30% 30%,
      #7fd1ff,
      #3dccc7 60%,
      transparent 70%
    );
  }
  &::after {
    width: 580px;
    height: 580px;
    right: -220px;
    bottom: -220px;
    background: radial-gradient(
      circle at 70% 70%,
      #ffb489,
      #ff7f50 55%,
      transparent 70%
    );
  }
`;

const HeaderContent = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: clamp(1rem, 4vw, 2.5rem);
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const HeroCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 16px;
  padding: clamp(1rem, 3.5vw, 1.5rem);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.25);
  backdrop-filter: saturate(135%) blur(10px);
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: clamp(0.75rem, 1.8vw, 0.85rem);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #c7e7f7;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.4rem 0.7rem;
  border-radius: 999px;
`;

const PageTitle = styled.h1`
  margin: 0.6rem 0 0.4rem;
  font-weight: 800;
  line-height: 1.08;
  font-size: clamp(1.6rem, 5vw, 2.4rem);
`;

const PageSubtitle = styled.p`
  margin: 0.25rem 0 1rem;
  opacity: 0.95;
  font-size: clamp(0.95rem, 2.2vw, 1.05rem);
  color: #e8f6ff;
`;

const HeroCTAs = styled.div`
  display: inline-flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.2rem;

  @media (max-width: 900px) {
    justify-content: center;
  }
`;

const PrimaryBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.85rem 1.15rem;
  border-radius: 12px;
  font-weight: 600;
  text-decoration: none;
  background: linear-gradient(45deg, #ff914d, #ff6b35);
  color: #fff;
  box-shadow: 0 8px 24px rgba(255, 107, 53, 0.35);
  transform: translateZ(0);
  transition: transform 180ms ease, box-shadow 180ms ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 12px 28px rgba(255, 107, 53, 0.45); }
`;

const GhostBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.85rem 1.1rem;
  border-radius: 12px;
  font-weight: 600;
  text-decoration: none;
  color: #d9f2ff;
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.06);
  transition: background 180ms ease, transform 180ms ease;
  &:hover { background: rgba(255, 255, 255, 0.12); transform: translateY(-2px); }
`;

/* Sections */
const Section = styled.section`
  padding: clamp(1.5rem, 4vw, 2.5rem) 1rem;
`;
const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

/* Past list (unchanged) */
const ListTitle = styled.h2`
  margin: 0 0 10px;
  font-weight: 800;
  color: #0f172a;
`;
const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: clamp(0.8rem, 2.5vw, 1.2rem);
`;
const PastCard = styled.div`
  grid-column: span 6;
  background: #ffffff;
  border: 1px solid #eef2f6;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
  transition: transform 160ms ease, box-shadow 160ms ease;
  &:hover { transform: translateY(-4px); box-shadow: 0 12px 26px rgba(15,23,42,0.09); }
  @media (max-width: 680px) { grid-column: span 12; }
`;
const PastHead = styled.div` display:flex; align-items:center; justify-content:space-between; gap:8px; `;
const PastTitle = styled.div` font-weight:800; color:#0f172a; `;
const PastSub = styled.div` color:#4b5563; font-size:.92rem; `;
const DatePill = styled.span`
  background:#e6f2ff; color:#1e3e52; border:1px solid #c9e2ff; border-radius:999px; padding:.25rem .6rem; font-weight:700; font-size:.8rem;
`;
const PastMeta = styled.div` color:#64748b; font-size:.9rem; margin-top:4px; `;

const ActionsRow = styled.div`
  display: flex; gap: 10px; margin-top: 12px; flex-wrap: wrap;
`;

const Action = styled.button`
  appearance: none;
  background: #ffffff;
  color: #1e3e52;
  border: 1px solid #c7d2fe;
  border-radius: 10px;
  padding: 8px 12px;
  font-weight: 700;
  cursor: pointer;
  line-height: 1;
  transition: background .15s ease, box-shadow .15s ease, transform .12s ease;
  box-shadow: 0 2px 0 rgba(30,62,82,0.08);
  &:hover { background: #f1f5ff; }
  &:active { transform: translateY(1px); }
`;

const ActionLink = styled.a`
  display: inline-flex; align-items: center; justify-content: center;
  text-decoration: none; background: #ffffff; color: #1e3e52;
  border: 1px solid #c7d2fe; border-radius: 10px; padding: 8px 12px;
  font-weight: 700; line-height: 1; transition: background .15s ease, box-shadow .15s ease, transform .12s ease;
  box-shadow: 0 2px 0 rgba(30,62,82,0.08);
  &:hover { background: #f1f5ff; }
  &:active { transform: translateY(1px); }
`;

/* Modals */
const ModalBg = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,.55);
  display: grid; place-items: center; padding: 16px; z-index: 2000;
`;
const Modal = styled.div`
  width: min(760px, 95vw); max-height: 85vh; overflow: auto;
  background: #0f1b2d; border: 1px solid #1e293b; border-radius: 16px;
  padding: 18px; color: #e6f0ff; box-shadow: 0 14px 40px rgba(0,0,0,.35);
`;
const ModalHead = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 8px;
`;
const ModalTitle = styled.h3`
  margin: 0; font-size: 1.1rem; font-weight: 800; color: #ffffff;
`;
const CloseX = styled.button`
  border: 0; background: transparent; color: #cfe7ff; font-weight: 900; font-size: 1.2rem; cursor: pointer;
  padding: 6px 8px; border-radius: 8px; &:hover { background: rgba(255,255,255,0.08); }
`;
const ModalBody = styled.div` color: #cfe7ff; font-size: .95rem; line-height: 1.62; `;
const BadgeRow = styled.div` display:flex; gap:8px; flex-wrap:wrap; margin:.25rem 0 .75rem; `;
const Badge = styled.span`
  display:inline-flex; align-items:center; gap:6px;
  background:#11243b; border:1px solid #28486a; color:#cfe7ff; padding:.3rem .55rem; border-radius:999px; font-size:.8rem; font-weight:700;
`;
const Divider = styled.hr` border: 0; height: 1px; background: #1e293b; margin: 10px 0 14px; `;
const LinkBar = styled.div` display:flex; gap:10px; flex-wrap:wrap; margin-top: 12px; `;
const LinkBtn = styled.a`
  display:inline-flex; align-items:center; gap:6px;
  color:#7fd1ff; font-weight:700; text-decoration:none; padding:.3rem .55rem; border:1px solid #214766; border-radius:10px; background:#0c2033;
  &:hover{ background:#0e2740; }
`;

/* Success modal (after register) */
const SuccessCard = styled(Modal)` width: min(560px, 95vw); `;

/* Clean empty-states */
const EmptyCard = styled.div`
  background: #ffffff; border: 1px solid #eef2f6; border-radius: 14px;
  padding: 16px; color: #475569;
`;

/* ===== API helpers ===== */
function useApi() {
  const base = API_BASE;

  const getUpcoming = useCallback(async () => {
    const r = await fetch(`${base}/api/webinars/upcoming`);
    return r.json();
  }, [base]);

  const getPast = useCallback(async () => {
    const r = await fetch(`${base}/api/webinars/past`);
    return r.json();
  }, [base]);

  return { getUpcoming, getPast };
}

/* ===== Helpers ===== */
function formatWhen(w) {
  if (!w?.startsAt) return "";
  return (
    new Date(w.startsAt).toLocaleString("en-IN", {
      timeZone: w.timezone || "Asia/Kolkata",
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }) + ` (${w.timezone || "Asia/Kolkata"})`
  );
}

export default function WebinarsPage() {
  const { getUpcoming, getPast } = useApi();
  const [upcoming, setUpcoming] = useState([]);
  const [past, setPast] = useState([]);
  const [selectedPast, setSelectedPast] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    (async () => {
      const u = await getUpcoming(); if (u?.ok) setUpcoming(u.data || []);
      const p = await getPast(); if (p?.ok) setPast(p.data || []);
    })();
  }, [getUpcoming, getPast]);

  const nextWebinar = upcoming?.[0];
  const whenString = useMemo(() => formatWhen(nextWebinar), [nextWebinar]);

  const openPastModal = (w) => setSelectedPast(w);
  const closePastModal = () => setSelectedPast(null);

  return (
    <>
      <Navbar />
      <PageShell>
        <Main>
          {/* Hero */}
          <HeaderSection>
            <HeaderContent>
              <HeroCard>
                <Eyebrow>Monthly Live Sessions • Construction + AI</Eyebrow>
                <PageTitle>Upcoming Webinars</PageTitle>
                <PageSubtitle>
                  Learn practical, construction-ready AI workflows from estimators, planners and PMs using real projects.
                </PageSubtitle>

                <HeroCTAs>
                  <PrimaryBtn href="#register">Register now</PrimaryBtn>
                  <GhostBtn href="#history">See past webinars</GhostBtn>
                </HeroCTAs>
              </HeroCard>

              {/* Quick summary on the right */}
              <HeroCard as="div" style={{ textAlign: "left" }}>
                {nextWebinar ? (
                  <>
                    <Eyebrow>Next Webinar</Eyebrow>
                    <div style={{ fontWeight: 800, fontSize: "1.1rem", marginTop: 6 }}>{nextWebinar.title}</div>
                    <div style={{ marginTop: 4, opacity: 0.9 }}>{nextWebinar.topic}</div>
                    <div style={{ marginTop: 8, fontWeight: 700 }}>{whenString}</div>
                  </>
                ) : (
                  <>
                    <Eyebrow>Next Webinar</Eyebrow>
                    <div style={{ fontWeight: 800, fontSize: "1.1rem", marginTop: 6 }}>Coming soon</div>
                    <div style={{ marginTop: 6, opacity: 0.9 }}>We’re lining up the next session.</div>
                  </>
                )}
              </HeroCard>
            </HeaderContent>
          </HeaderSection>

          {/* Combined Details + Form card */}
          <Section id="register">
            <Inner>
              {nextWebinar ? (
                <WebinarSignupForm
                  webinar={nextWebinar}
                  onSuccess={() => setShowSuccess(true)}
                  title="Save your seat"
                />
              ) : (
                <EmptyCard>
                  <strong>No upcoming webinar yet.</strong>
                  <div style={{ marginTop: 6 }}>
                    We’re lining up the next session. Check back soon or join our email list on the homepage.
                  </div>
                </EmptyCard>
              )}
            </Inner>
          </Section>

          {/* Past webinars */}
          <Section id="history">
            <Inner>
              <ListTitle>Past Webinars</ListTitle>
              {past.length === 0 ? (
                <EmptyCard>No past webinars yet. As soon as a session completes, you’ll see the recap and recording here.</EmptyCard>
              ) : (
                <Cards>
                  {past.map((w) => {
                    const dateStr = new Date(w.startsAt).toLocaleString("en-IN", {
                      timeZone: w.timezone || "Asia/Kolkata",
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    });
                    const metaBits = [
                      w.durationMins ? `${w.durationMins} mins` : null,
                      (w.hostNames && w.hostNames.length) ? `Hosts: ${w.hostNames.join(", ")}` : null,
                      w.timezone ? `TZ: ${w.timezone}` : null,
                    ].filter(Boolean).join(" • ");

                    return (
                      <PastCard key={w._id}>
                        <PastHead>
                          <PastTitle>{w.title}</PastTitle>
                          <DatePill>{dateStr}</DatePill>
                        </PastHead>
                        <PastSub>{w.topic}</PastSub>
                        {metaBits && <PastMeta>{metaBits}</PastMeta>}
                        <ActionsRow>
                          <Action onClick={() => openPastModal(w)}>View recap</Action>
                          {w.recordingUrl && (
                            <ActionLink
                              href={w.recordingUrl}
                              target="_blank"
                              rel="noreferrer"
                              aria-label={`Watch recording of ${w.title}`}
                            >
                              Watch recording
                            </ActionLink>
                          )}
                        </ActionsRow>
                      </PastCard>
                    );
                  })}
                </Cards>
              )}
            </Inner>
          </Section>
        </Main>

        <Footer />
        <PromptRibbon />
      </PageShell>

      {/* Past Webinar Details Modal */}
      {selectedPast && (
        <ModalBg onClick={closePastModal}>
          <Modal onClick={(e) => e.stopPropagation()} aria-live="polite">
            <ModalHead>
              <ModalTitle>{selectedPast.title}</ModalTitle>
              <CloseX aria-label="Close" onClick={closePastModal}>×</CloseX>
            </ModalHead>

            <BadgeRow>
              <Badge>📅 {new Date(selectedPast.startsAt).toLocaleString("en-IN", {
                timeZone: selectedPast.timezone || "Asia/Kolkata",
                year: "numeric", month: "short", day: "numeric"
              })}</Badge>
              {selectedPast.durationMins && <Badge>⏱ {selectedPast.durationMins} mins</Badge>}
              {selectedPast.hostNames?.length ? <Badge>👤 {selectedPast.hostNames.join(", ")}</Badge> : null}
              {selectedPast.timezone && <Badge>🕒 {selectedPast.timezone}</Badge>}
            </BadgeRow>

            <ModalBody>
              {selectedPast.topic && (
                <>
                  <strong style={{ color: "#fff" }}>Topic:</strong> {selectedPast.topic}
                  <Divider />
                </>
              )}

              {selectedPast.highlights && (
                <>
                  <strong style={{ color: "#fff" }}>Highlights:</strong>
                  <div style={{ marginTop: 6 }}>{selectedPast.highlights}</div>
                  <Divider />
                </>
              )}

              {selectedPast.recapHtml && (
                <>
                  <strong style={{ color: "#fff" }}>Recap:</strong>
                  <div
                    style={{ marginTop: 6 }}
                    dangerouslySetInnerHTML={{ __html: selectedPast.recapHtml }}
                  />
                  <Divider />
                </>
              )}

              <LinkBar>
                {selectedPast.recordingUrl && (
                  <LinkBtn href={selectedPast.recordingUrl} target="_blank" rel="noreferrer">
                    ▶ Watch recording
                  </LinkBtn>
                )}
                {selectedPast.slideDeckUrl && (
                  <LinkBtn href={selectedPast.slideDeckUrl} target="_blank" rel="noreferrer">
                    ⬇ Download slides
                  </LinkBtn>
                )}
              </LinkBar>
            </ModalBody>
          </Modal>
        </ModalBg>
      )}

      {/* Success Modal */}
      {showSuccess && (
        <ModalBg onClick={() => setShowSuccess(false)}>
          <SuccessCard onClick={(e) => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>You're registered!</ModalTitle>
              <CloseX aria-label="Close" onClick={() => setShowSuccess(false)}>×</CloseX>
            </ModalHead>
            <ModalBody>
              <p style={{ margin: "6px 0 10px" }}>
                We’ve emailed you the joining link and a calendar invite for <strong>{nextWebinar?.title}</strong>.
              </p>
              {whenString && (
                <p style={{ margin: 0 }}>
                  <strong>When:</strong> {whenString}
                </p>
              )}
            </ModalBody>
            <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
              <Action onClick={() => setShowSuccess(false)}>Done</Action>
              <Action onClick={() => { setShowSuccess(false); window.location.hash = "#history"; }}>
                See past webinars
              </Action>
            </div>
          </SuccessCard>
        </ModalBg>
      )}
    </>
  );
}
